import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { getPoPayments, createPayment, updatePayment, deletePayment } from "../api";
import { lightTable } from "../../../../../../../../styles/muiProps";

const initialForm = {
  paymentDate: "",
  amount: "",
  forWhom: "",
  invoiceAmount: "",
  remarks: "",
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  } catch (error) {
    return dateString.split("T")[0] || "-";
  }
};

const columns = [
  { header: "Date", accessorKey: "paymentDate", Cell: ({ cell }) => cell.getValue() || "-" },
  { header: "Amount", accessorKey: "amount", Cell: ({ cell }) => cell.getValue() ?? "-" },
  {
    header: "Remarks",
    accessorKey: "remarks",
    Cell: ({ row }) =>
      row.original.remarks ?? row.original.invoiceAmount ?? "-",
  },
];

export default function PaymentModal({ poId, po }) {
  const [form, setForm] = React.useState(initialForm);
  const [payments, setPayments] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingPayment, setEditingPayment] = React.useState(null);
  const [editForm, setEditForm] = React.useState(initialForm);

  // Derive vendor name directly from PO details (no manual selection)
  const vendorName = React.useMemo(() => {
    if (!po) return "";
    if (po.vendorName) return po.vendorName;
    return po.poMaterials?.[0]?.vendor?.name || "";
  }, [po]);

  // Calculate total PO cost from poMaterials
  const totalPOCost = React.useMemo(() => {
    if (!po?.poMaterials || !Array.isArray(po.poMaterials)) return 0;
    return po.poMaterials.reduce((total, material) => {
      const cost = parseFloat(material.cost) || 0;
      return total + cost;
    }, 0);
  }, [po]);

  const fetchPayments = React.useCallback(async () => {
    if (!poId) return;
    setLoading(true);
    try {
      const data = await getPoPayments(poId);
      setPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load payments:", error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [poId]);

  React.useEffect(() => {
    if (poId) {
      fetchPayments();
    }
  }, [poId, fetchPayments]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    if (!poId) return;
    try {
      await createPayment({
        ...form,
        poId,
        forWhom: vendorName,
        remarks: form.remarks,
      });
      setForm(initialForm);
      fetchPayments();
    } catch (error) {
      console.error("Failed to create payment:", error);
      alert("Failed to create payment. Please try again.");
    }
  };

  const handleOpenEdit = (payment) => {
    setEditingPayment(payment);
    setEditForm({
      paymentDate: payment.paymentDate?.split?.("T")?.[0] || payment.paymentDate || "",
      amount: String(payment.amount ?? ""),
      forWhom: payment.forWhom || "",
      invoiceAmount: payment.invoiceAmount ?? "",
      remarks: payment.remarks ?? "",
    });
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditingPayment(null);
    setEditForm(initialForm);
  };

  const handleEditChange = (field) => (event) => {
    setEditForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleEditSave = async () => {
    if (!editingPayment?.id) return;
    try {
      await updatePayment(editingPayment.id, {
        paymentDate: editForm.paymentDate,
        amount: editForm.amount,
        invoiceAmount: editForm.invoiceAmount,
        remarks: editForm.remarks,
      });
      handleCloseEdit();
      fetchPayments();
    } catch (error) {
      console.error("Failed to update payment:", error);
      alert("Failed to update payment. Please try again.");
    }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm("Are you sure you want to delete this payment?")) return;
    try {
      await deletePayment(paymentId);
      fetchPayments();
    } catch (error) {
      console.error("Failed to delete payment:", error);
      alert("Failed to delete payment. Please try again.");
    }
  };

  const table = useMaterialReactTable({
    data: payments,
    columns,
    getRowId: (row) => String(row.id),
    ...lightTable,
    muiTableContainerProps: { sx: { maxHeight: 400 } },
    enableRowActions: true,
    positionActionsColumn: "last",
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Tooltip title="Edit">
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleOpenEdit(row.original)}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            color="error"
            size="small"
            onClick={() => handleDelete(row.original.id)}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  });

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
        Payments
      </Typography>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
          Add New Payment
        </Typography>
        {vendorName && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Vendor: {vendorName}
          </Typography>
        )}
        <Stack direction="row" spacing={2}>
          <TextField
            label="Date"
            type="date"
            value={form.paymentDate}
            onChange={handleChange("paymentDate")}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
          <TextField
            label="Amount"
            value={form.amount}
            onChange={handleChange("amount")}
            fullWidth
            size="small"
            type="number"
          />
          <TextField
            label="Remarks"
            value={form.remarks}
            onChange={handleChange("remarks")}
            fullWidth
            size="small"
          />
        </Stack>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button variant="contained" onClick={handleSave}>
            Save Payment
          </Button>
        </Box>
      </Stack>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2 }}>
          Existing Payments
        </Typography>
        {loading ? (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Loading...
          </Typography>
        ) : payments.length > 0 ? (
          <MaterialReactTable table={table} />
        ) : (
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            No payments recorded yet.
          </Typography>
        )}
      </Box>

      {/* Edit Payment Dialog */}
      <Dialog open={editOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Payment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 2 }}>
            <TextField
              label="Date"
              type="date"
              value={editForm.paymentDate}
              onChange={handleEditChange("paymentDate")}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Amount"
              value={editForm.amount}
              onChange={handleEditChange("amount")}
              fullWidth
              size="small"
              type="number"
            />
            <TextField
              label="Invoice Amount"
              value={editForm.invoiceAmount}
              onChange={handleEditChange("invoiceAmount")}
              fullWidth
              size="small"
            />
            <TextField
              label="Remarks"
              value={editForm.remarks}
              onChange={handleEditChange("remarks")}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
